// frontend/app/components/admin/useLessonDocument.js
//
// Everything the lesson editor does, separated from everything it draws: load
// the document, hold the draft, save it, hand it back to the seeder.
//
// After a save the server's own document replaces the draft rather than the
// draft being kept. What the server stored is the only honest answer to "did
// that land", and it is what carries the new lock back.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../AuthContext";
import {
  getAdminLessonPlan,
  readAdminLessonError,
  saveAdminLessonPlan,
  unlockAdminLessonPlan,
} from "../../lessonAdmin";
import { apiGetTopicAssetCatalog } from "../../lessonPlans";
import { hasUnsavedChanges, toDraft, toPayload, validateDraft } from "./lessonAdminDraft";

const useLessonDocument = (lessonPlanId, { onSaved } = {}) => {
  const { fetchWithAuth } = useAuth() || {};

  const [draft, setDraft] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [problems, setProblems] = useState([]);
  const [savedNote, setSavedNote] = useState(null);

  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);

  const [catalog, setCatalog] = useState(null);

  const adopt = useCallback((document) => {
    const next = toDraft(document);
    setDraft(next);
    setBaseline(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const document = await getAdminLessonPlan(lessonPlanId);
        if (!cancelled) adopt(document);
      } catch (err) {
        if (!cancelled) setLoadError(readAdminLessonError(err, "Could not load this lesson."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lessonPlanId, adopt]);

  // Held in a ref, and deliberately not a dependency below: AuthContext builds
  // a new fetchWithAuth on every render of the provider, so depending on it
  // would refetch the catalogue for a change that has nothing to do with the
  // topic.
  const fetchRef = useRef(fetchWithAuth);
  fetchRef.current = fetchWithAuth;

  // The topic's real 3D scenes and video segments, so an asset reference reads
  // as "Overview scene" instead of "PWO 215". Failing to load it is not worth
  // reporting: BlockEditor falls back to manual ids, which is what an admin
  // editing text needs anyway.
  useEffect(() => {
    const board = draft?.base_board;
    const topicId = draft?.board_topic_id;
    const sourceTopicId = draft?.source_topic_id ?? topicId;
    if (!fetchRef.current || !board || !topicId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const next = await apiGetTopicAssetCatalog(fetchRef.current, {
          board,
          boardTopicId: topicId,
          sourceTopicId,
        });
        if (!cancelled) setCatalog(next);
      } catch {
        if (!cancelled) setCatalog(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [draft?.base_board, draft?.board_topic_id, draft?.source_topic_id]);

  const dirty = useMemo(
    () => Boolean(draft && baseline) && hasUnsavedChanges(draft, baseline),
    [draft, baseline]
  );

  const change = useCallback((next) => {
    setDraft(next);
    // A stale "Saved" line next to an unsaved change is the one thing this
    // screen must never show.
    setSavedNote(null);
  }, []);

  const save = useCallback(async () => {
    if (saving || !draft) return false;

    const found = validateDraft(draft);
    setProblems(found);
    if (found.length) {
      setSaveError(null);
      setSavedNote(null);
      return false;
    }

    setSaving(true);
    setSaveError(null);
    setSavedNote(null);
    try {
      const written = await saveAdminLessonPlan(lessonPlanId, toPayload(draft));
      // A route that answers 204, or an empty body, still saved something —
      // re-read rather than assume the draft is what is now stored.
      const document = written && written.id ? written : await getAdminLessonPlan(lessonPlanId);
      adopt(document);
      setSavedNote("Saved. This lesson is now locked, so seeding will leave it alone.");
      if (onSaved) onSaved();
      return true;
    } catch (err) {
      setSaveError(readAdminLessonError(err, "The lesson was not saved."));
      return false;
    } finally {
      setSaving(false);
    }
  }, [adopt, draft, lessonPlanId, onSaved, saving]);

  const unlock = useCallback(async () => {
    if (unlocking) return false;

    setUnlocking(true);
    setUnlockError(null);
    try {
      await unlockAdminLessonPlan(lessonPlanId);
      // The unlock response is not the document; re-read so the lock shown is
      // the lock stored.
      adopt(await getAdminLessonPlan(lessonPlanId));
      setSavedNote("Unlocked. The next seed will rewrite this lesson from its file.");
      if (onSaved) onSaved();
      return true;
    } catch (err) {
      setUnlockError(readAdminLessonError(err, "The lesson was not unlocked."));
      return false;
    } finally {
      setUnlocking(false);
    }
  }, [adopt, lessonPlanId, onSaved, unlocking]);

  const dismissUnlockError = useCallback(() => setUnlockError(null), []);

  return {
    draft,
    loading,
    loadError,
    dirty,
    catalog,
    change,
    save,
    saving,
    saveError,
    problems,
    savedNote,
    unlock,
    unlocking,
    unlockError,
    dismissUnlockError,
  };
};

export default useLessonDocument;
